import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import {
  Briefcase, GraduationCap, TrendingUp, DollarSign, MapPin, Clock,
  Loader2, ArrowRight, Building2, Award, Globe, Sparkles, Users,
  BookOpen, Target, CheckCircle, Lightbulb, ChevronRight, Rocket, Coins
} from 'lucide-react';
import { AI_PERSONAS } from '../constants/aiPersonas';
import { CityInsights } from '../components/career/CityInsights';

const CareerSimulator = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState('select'); // select, loading, results
  const [popularPaths, setPopularPaths] = useState(null);
  const [selectedMajor, setSelectedMajor] = useState('');
  const [customMajor, setCustomMajor] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('United States');
  const [customCountry, setCustomCountry] = useState('');
  const [careerGoal, setCareerGoal] = useState('');
  const [careerPath, setCareerPath] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check if user is logged in
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPopularPaths();
  }, []);

  const fetchPopularPaths = async () => {
    try {
      const response = await api.get('/career-simulator/popular-paths');
      setPopularPaths(response.data);
    } catch (error) {
      console.error('Failed to fetch popular paths:', error);
    }
  };

  const generateCareerPath = async () => {
    const major = selectedMajor || customMajor;
    if (!major) {
      toast({ title: "Required", description: "Please select or enter a major", variant: "destructive" });
      return;
    }

    // Check if user is logged in - redirect to pricing if not
    if (!token) {
      toast({ title: "Credits Required", description: "Please purchase credits to use this feature", variant: "default" });
      navigate('/pricing');
      return;
    }

    // Use custom country if entered, otherwise use selected country
    const targetCountry = customCountry.trim() || selectedCountry;

    try {
      setLoading(true);
      setStage('loading');
      
      const response = await api.post('/career-simulator/generate', {
        major,
        target_country: targetCountry,
        career_goal: careerGoal || null,
        user_id: user?._id || null
      });
      
      setCareerPath(response.data);
      setStage('results');
    } catch (error) {
      // If 402 (payment required), redirect to pricing
      if (error.response?.status === 402) {
        toast({ title: "Credits Required", description: "Please purchase credits to use this feature", variant: "default" });
        navigate('/pricing');
      } else {
        toast({ title: "Error", description: "Failed to generate career path", variant: "destructive" });
      }
      setStage('select');
    } finally {
      setLoading(false);
    }
  };

  const resetSimulator = () => {
    setStage('select');
    setSelectedMajor('');
    setCustomMajor('');
    setCustomCountry('');
    setCareerGoal('');
    setCareerPath(null);
  };

  const path = careerPath?.career_path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-500 rounded-full blur-3xl" />
        </div>
        <div className="max-w-5xl mx-auto px-4 py-16 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl mb-6 shadow-lg shadow-purple-500/30 overflow-hidden border-4 border-white/30">
              <img 
                src={AI_PERSONAS.ASHISH.avatar} 
                alt="Ashish - Career Simulator"
                className="w-full h-full object-cover"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Meet Ashish - Career Path Simulator</h1>
            <p className="text-xl text-purple-200 max-w-2xl mx-auto">
              Visualize your journey from graduation to your dream career with salary projections and milestones
            </p>
            <div className="flex justify-center gap-3 mt-6">
              <Badge className="bg-purple-500/30 text-purple-200 border-purple-400/30">
                <Sparkles className="h-3 w-3 mr-1" /> AI Powered
              </Badge>
              <Badge className="bg-pink-500/30 text-pink-200 border-pink-400/30">
                <Globe className="h-3 w-3 mr-1" /> Country-Specific
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-16 -mt-8">
        {/* Selection Stage */}
        {stage === 'select' && (
          <div className="space-y-8">
            {/* Major Selection */}
            <Card className="border-0 shadow-2xl bg-white/10 backdrop-blur-lg border border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <GraduationCap className="h-6 w-6 text-purple-400" />
                  Choose Your Major
                </CardTitle>
              </CardHeader>
              <CardContent>
                {popularPaths && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {popularPaths.popular_paths.map((path) => (
                      <button
                        key={path.major}
                        data-testid={`major-btn-${path.major.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => {
                          setSelectedMajor(path.major);
                          setCustomMajor('');
                        }}
                        className={`p-4 rounded-xl text-left transition-all ${
                          selectedMajor === path.major
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30'
                            : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
                        }`}
                      >
                        <span className="text-2xl mb-2 block">{path.icon}</span>
                        <span className="font-medium block">{path.major}</span>
                        <span className="text-xs opacity-70 block mt-1">
                          {path.careers[0]}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                
                <div className="relative">
                  <Input
                    data-testid="custom-major-input"
                    placeholder="Or type a custom major..."
                    value={customMajor}
                    onChange={(e) => {
                      setCustomMajor(e.target.value);
                      setSelectedMajor('');
                    }}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Country & Goal Selection */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-0 shadow-2xl bg-white/10 backdrop-blur-lg border border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-pink-400" />
                    Target Country
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {popularPaths?.countries.map((country) => (
                      <button
                        key={country.code}
                        data-testid={`country-btn-${country.code.toLowerCase()}`}
                        onClick={() => {
                          setSelectedCountry(country.name);
                          setCustomCountry('');
                        }}
                        className={`p-3 rounded-lg flex items-center gap-2 transition-all ${
                          selectedCountry === country.name && !customCountry
                            ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                      >
                        <span className="text-xl">{country.flag}</span>
                        <span className="text-sm font-medium">{country.name}</span>
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <Input
                      placeholder="Or type any other country..."
                      value={customCountry}
                      onChange={(e) => {
                        setCustomCountry(e.target.value);
                        if (e.target.value) setSelectedCountry('');
                      }}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                    {customCountry && (
                      <p className="text-xs text-pink-300 mt-2">
                        Using custom country: {customCountry}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-2xl bg-white/10 backdrop-blur-lg border border-white/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-400" />
                    Career Goal (Optional)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    placeholder="e.g., Software Engineer at Google"
                    value={careerGoal}
                    onChange={(e) => setCareerGoal(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                  <p className="text-sm text-white/50 mt-2">
                    Leave empty to see the most common career path
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Generate Button */}
            <div className="text-center">
              <Button
                data-testid="generate-career-path-btn"
                onClick={generateCareerPath}
                disabled={!selectedMajor && !customMajor}
                size="lg"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-12 py-6 text-lg shadow-xl shadow-purple-500/30"
              >
                <Rocket className="h-5 w-5 mr-2" />
                Generate My Career Path
              </Button>
            </div>
          </div>
        )}

        {/* Loading Stage */}
        {stage === 'loading' && (
          <Card className="border-0 shadow-2xl bg-white/10 backdrop-blur-lg border border-white/20">
            <CardContent className="py-20 text-center">
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-ping opacity-20" />
                <div className="relative w-24 h-24 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <Loader2 className="h-12 w-12 text-white animate-spin" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Simulating Your Career Path</h2>
              <p className="text-purple-200">Analyzing job markets, salary data, and career trajectories...</p>
            </CardContent>
          </Card>
        )}

        {/* Results Stage */}
        {stage === 'results' && path && !path.parse_error && (
          <div className="space-y-6">
            {/* Header Card */}
            <Card className="border-0 shadow-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white overflow-hidden">
              <CardContent className="py-8">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <Badge className="bg-white/20 text-white mb-2">Career Path</Badge>
                    <h2 className="text-3xl font-bold">{path.career_title}</h2>
                    <p className="text-white/80 mt-1">{path.industry} • {careerPath.target_country}</p>
                  </div>
                  <Button onClick={resetSimulator} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                    Simulate Another
                  </Button>
                </div>
                {path.path_summary && (
                  <p className="mt-4 text-white/90 text-lg">{path.path_summary}</p>
                )}
              </CardContent>
            </Card>

            {/* Timeline */}
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-500 via-pink-500 to-purple-500 rounded-full hidden md:block" />
              
              <div className="space-y-6">
                {/* Education Phase */}
                {path.education_phase && (
                  <Card className="border-0 shadow-xl bg-white/10 backdrop-blur-lg border border-white/20 md:ml-16">
                    <div className="absolute -left-4 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center hidden md:flex">
                      <GraduationCap className="h-4 w-4 text-white" />
                    </div>
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <GraduationCap className="h-5 w-5 text-purple-400 md:hidden" />
                        Education Phase
                        <Badge className="bg-purple-500/30 text-purple-200">{path.education_phase.duration}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-white/80">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-white mb-2">{path.education_phase.degree}</h4>
                          <p className="text-sm text-purple-300 mb-3">Est. Cost: {path.education_phase.estimated_cost}</p>
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-white">Key Courses:</p>
                            <div className="flex flex-wrap gap-1">
                              {path.education_phase.key_courses?.map((course, i) => (
                                <Badge key={i} className="bg-white/10 text-white/80 text-xs">{course}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white mb-2">Skills Gained:</p>
                          <ul className="space-y-1">
                            {path.education_phase.skills_gained?.map((skill, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm">
                                <CheckCircle className="h-3 w-3 text-green-400" />
                                {skill}
                              </li>
                            ))}
                          </ul>
                          {path.education_phase.certifications?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-sm font-medium text-white mb-1">Certifications:</p>
                              <div className="flex flex-wrap gap-1">
                                {path.education_phase.certifications.map((cert, i) => (
                                  <Badge key={i} className="bg-purple-500/30 text-purple-200 text-xs">{cert}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Internship Phase */}
                {path.internship_phase && (
                  <Card className="border-0 shadow-xl bg-white/10 backdrop-blur-lg border border-white/20 md:ml-16">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-pink-400" />
                        Internships
                        <Badge className="bg-pink-500/30 text-pink-200">{path.internship_phase.duration}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-white/80">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-white mb-2">Typical Roles:</p>
                          <ul className="space-y-1">
                            {path.internship_phase.typical_roles?.map((role, i) => (
                              <li key={i} className="text-sm">{role}</li>
                            ))}
                          </ul>
                          <p className="mt-3 text-green-400 font-semibold">{path.internship_phase.hourly_pay}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white mb-2">Target Companies:</p>
                          <div className="flex flex-wrap gap-1">
                            {path.internship_phase.companies?.map((company, i) => (
                              <Badge key={i} className="bg-white/10 text-white/80">{company}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Career Levels */}
                {['entry_level', 'mid_level', 'senior_level', 'executive_level'].map((level, idx) => {
                  const data = path[level];
                  if (!data) return null;
                  
                  const icons = [Briefcase, TrendingUp, Award, Rocket];
                  const colors = ['blue', 'green', 'yellow', 'red'];
                  const Icon = icons[idx];
                  
                  return (
                    <Card key={level} className="border-0 shadow-xl bg-white/10 backdrop-blur-lg border border-white/20 md:ml-16">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Icon className={`h-5 w-5 text-${colors[idx]}-400`} />
                          {level.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          <Badge className={`bg-${colors[idx]}-500/30 text-${colors[idx]}-200`}>{data.years}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-white/80">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <p className="text-sm font-medium text-white mb-2">Titles:</p>
                            <ul className="space-y-1 mb-3">
                              {data.titles?.map((title, i) => (
                                <li key={i} className="text-sm font-medium">{title}</li>
                              ))}
                            </ul>
                            <p className="text-2xl font-bold text-green-400">{data.salary_range}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white mb-2">Key Responsibilities:</p>
                            <ul className="space-y-1">
                              {data.responsibilities?.map((resp, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <ChevronRight className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />
                                  {resp}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {/* City Insights (US only) */}
                {/united states|^usa?$/i.test(careerPath.target_country || '') && (
                  <CityInsights careerTitle={path.career_title} major={careerPath.major} />
                )}

                {/* Job Market & Visa */}
                <div className="grid md:grid-cols-2 gap-6 md:ml-16">
                  {path.job_market_outlook && (
                    <Card className="border-0 shadow-xl bg-white/10 backdrop-blur-lg border border-white/20">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-green-400" />
                          Job Market Outlook
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-white/80 space-y-3">
                        <div className="flex justify-between">
                          <span>Growth Rate:</span>
                          <span className="font-semibold text-green-400">{path.job_market_outlook.growth_rate}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Demand:</span>
                          <Badge className="bg-green-500/30 text-green-200">{path.job_market_outlook.demand_level}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Remote:</span>
                          <Badge className="bg-blue-500/30 text-blue-200">{path.job_market_outlook.remote_opportunities}</Badge>
                        </div>
                        <div>
                          <p className="text-sm mb-1">Top Locations:</p>
                          <div className="flex flex-wrap gap-1">
                            {path.job_market_outlook.top_locations?.map((loc, i) => (
                              <Badge key={i} className="bg-white/10 text-white/80 text-xs">{loc}</Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {path.visa_considerations && (
                    <Card className="border-0 shadow-xl bg-white/10 backdrop-blur-lg border border-white/20">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Globe className="h-5 w-5 text-blue-400" />
                          Visa Considerations
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-white/80 space-y-3">
                        <div className="flex justify-between">
                          <span>H-1B Friendly:</span>
                          <Badge className={path.visa_considerations.h1b_friendly ? "bg-green-500/30 text-green-200" : "bg-red-500/30 text-red-200"}>
                            {path.visa_considerations.h1b_friendly ? "Yes" : "Limited"}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Sponsorship:</span>
                          <Badge className="bg-purple-500/30 text-purple-200">{path.visa_considerations.sponsorship_likelihood}</Badge>
                        </div>
                        <div>
                          <p className="text-sm mb-1">Visa Paths:</p>
                          <div className="flex flex-wrap gap-1">
                            {path.visa_considerations.common_visa_paths?.map((visa, i) => (
                              <Badge key={i} className="bg-white/10 text-white/80 text-xs">{visa}</Badge>
                            ))}
                          </div>
                        </div>
                        {path.visa_considerations.tips && (
                          <p className="text-sm text-yellow-300 flex items-start gap-2">
                            <Lightbulb className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            {path.visa_considerations.tips}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Alternative Paths & Tips */}
                <div className="grid md:grid-cols-2 gap-6 md:ml-16">
                  {path.alternative_paths?.length > 0 && (
                    <Card className="border-0 shadow-xl bg-white/10 backdrop-blur-lg border border-white/20">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Target className="h-5 w-5 text-yellow-400" />
                          Alternative Careers
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {path.alternative_paths.map((alt, i) => (
                          <div key={i} className="p-3 bg-white/5 rounded-lg">
                            <p className="font-medium text-white">{alt.title}</p>
                            <p className="text-sm text-white/60">{alt.description}</p>
                            <p className="text-sm text-green-400 mt-1">{alt.salary_range}</p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {path.success_tips?.length > 0 && (
                    <Card className="border-0 shadow-xl bg-white/10 backdrop-blur-lg border border-white/20">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Lightbulb className="h-5 w-5 text-yellow-400" />
                          Success Tips
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {path.success_tips.map((tip, i) => (
                            <li key={i} className="flex items-start gap-3 text-white/80">
                              <div className="w-6 h-6 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-yellow-400">{i + 1}</span>
                              </div>
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>

            {/* CTA */}
            <Card className="border-0 shadow-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-lg border border-white/20">
              <CardContent className="py-8 text-center">
                <h3 className="text-xl font-bold text-white mb-2">Ready to Start Your Journey?</h3>
                <p className="text-white/70 mb-6">Get personalized guidance from Faith, our AI Academic Advisor</p>
                <div className="flex justify-center gap-4">
                  <Link to="/academic-advisor">
                    <Button className="bg-gradient-to-r from-purple-500 to-pink-500">
                      Talk to Faith
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/colleges">
                    <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                      Browse Colleges
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Raw Response Fallback */}
        {stage === 'results' && path?.parse_error && (
          <Card className="border-0 shadow-xl bg-white/10 backdrop-blur-lg border border-white/20">
            <CardHeader>
              <CardTitle className="text-white">Career Path Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-white/80 whitespace-pre-wrap">{path.raw_response}</p>
              <Button onClick={resetSimulator} className="mt-4">
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CareerSimulator;
